import { useEffect } from 'react';
import { GetServerSideProps } from 'next';
import { GetProfileUserByNicknameResult } from '@/infra/api/user/types/GetProfileUserByNickname';
import { getProfileTitle } from '@/presentation/services/profile';
import { GetCommentsByNicknameResult } from '@/infra/api/user/types/GetCommentsByNickname';
import { GetDealsByNicknameResult } from '@/infra/api/user/types/GetDealsByNickname';
import {
  ProfilePageTabs,
  getProfileUrl,
} from '@/presentation/services/urls/profile';
import EnhancedProfilePageContainer from '../../../presentation/pages/secure/profile';
import EnhancedDefaultContent from '../../../presentation/pages/secure/profile/components/DefaultContent';
import EmptyProfilePage from '../../../presentation/pages/secure/profile/components/EmptyProfilePage';
import cachePoliciesLoader from '../../../server/loaders/cachePolicies';
import SEO from '../../../presentation/components/SEO';
import { getProfileUserByNickname } from '../../../infra/api/user/endpoints';
import { useApiCache } from '../../../presentation/hooks/useApiCache';
import {
  dealsCacheWithVote$,
  getDealsByNickname,
  getDealsByNicknameSSR,
  populateDealsByNicknameFromSSR,
} from '../../../infra/api/user/helpers/getDealsByNickname';
import {
  commentsCacheWithVote$,
  getCommentsByNickname,
  getCommentsByNicknameSSR,
  populateCommentsByNicknameFromSSR,
} from '../../../infra/api/user/helpers/getCommentsByNickname';
import { getCanonicalUrl } from '../../../presentation/services/urls';

type ProfilePageProps = {
  profile?: GetProfileUserByNicknameResult;
  nickname: string;
  commentsSSR: GetCommentsByNicknameResult | null;
  dealsSSR: GetDealsByNicknameResult | null;
  titleSSR: string;
  tab: ProfilePageTabs;
};

function ProfilePage({
  profile,
  nickname,
  commentsSSR,
  dealsSSR,
  titleSSR,
  tab,
}: ProfilePageProps) {
  const canonical = getCanonicalUrl(getProfileUrl(nickname, { tab }));

  const {
    deals,
    loading: dealsLoading,
    pageInfo: dealsPageInfo,
  } = useApiCache(dealsCacheWithVote$) || {};

  const {
    comments,
    loading: commentsLoading,
    pageInfo: commentsPageInfo,
  } = useApiCache(commentsCacheWithVote$) || {};

  useEffect(() => {
    if (dealsSSR) populateDealsByNicknameFromSSR(nickname, dealsSSR);
    if (commentsSSR) populateCommentsByNicknameFromSSR(nickname, commentsSSR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nickname, tab]);

  const handleLoadMoreComments = () => {
    getCommentsByNickname(nickname, commentsPageInfo?.endCursor);
  };

  const handleLoadMoreDeals = () => {
    getDealsByNickname(nickname, dealsPageInfo?.endCursor);
  };

  if (!profile) {
    return <EmptyProfilePage />;
  }

  const robots =
    profile.isVerified && tab === ProfilePageTabs.Deals
      ? 'index, follow'
      : 'noindex, follow';

  return (
    <>
      <SEO title={titleSSR} robots={robots} canonical={canonical} />
      <EnhancedProfilePageContainer profileUser={profile}>
        <EnhancedDefaultContent
          tab={tab}
          profileUser={profile}
          comments={comments || commentsSSR?.edges || []}
          commentsHasNextPage={commentsPageInfo?.hasNextPage}
          commentsLoading={commentsLoading}
          onLoadMoreComments={handleLoadMoreComments}
          deals={deals || dealsSSR?.edges || []}
          dealsHasNextPage={dealsPageInfo?.hasNextPage}
          dealsLoading={dealsLoading}
          onLoadMoreDeals={handleLoadMoreDeals}
        />
      </EnhancedProfilePageContainer>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/require-await
export const getServerSideProps: GetServerSideProps<ProfilePageProps> = async ({
  res,
  params,
  query,
}) => {
  res.setHeader('Cache-Control', cachePoliciesLoader('/perfil/*'));

  const { tab = ProfilePageTabs.Deals } = query as { tab?: ProfilePageTabs };
  const nickname = ((params?.nickname as string) || '').toLowerCase();

  let profile: GetProfileUserByNicknameResult | undefined;
  try {
    const { data } = await getProfileUserByNickname.requestAsPromise({
      nickname,
    });
    profile = data;
  } catch {
    profile = undefined;
  }

  const title = getProfileTitle(nickname);

  let deals: GetDealsByNicknameResult | null = null;
  let comments: GetCommentsByNicknameResult | null = null;

  if (tab === ProfilePageTabs.Comments) {
    comments = (await getCommentsByNicknameSSR(nickname)) ?? null;
  } else {
    deals = (await getDealsByNicknameSSR(nickname)) ?? null;
  }

  return {
    props: {
      profile,
      nickname,
      titleSSR: title,
      dealsSSR: deals,
      commentsSSR: comments,
      tab,
    },
  };
};

export default ProfilePage;
